/**
 * Viewport detection — warns users on small screens.
 * Sentinel is a desktop-first OSINT tool. Mobile/tablet layouts are not supported,
 * but a graceful overlay is better than a broken UI.
 */

const MIN_WIDTH = 1024;
const DISMISS_KEY = 'sentinel-viewport-dismissed';

let overlayEl: HTMLElement | null = null;

/** Check if the overlay was previously dismissed by the user. */
function isDismissed(): boolean {
  return localStorage.getItem(DISMISS_KEY) === '1';
}

/** Create the viewport warning overlay element. */
function createOverlay(): HTMLElement {
  const overlay = document.createElement('div');
  overlay.id = 'viewport-warning';
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.setAttribute('aria-label', 'Small viewport warning');

  const card = document.createElement('div');
  card.className = 'viewport-warning-card';

  const icon = document.createElement('div');
  icon.className = 'viewport-warning-icon';
  icon.textContent = '\u26A0'; // ⚠
  card.appendChild(icon);

  const heading = document.createElement('h2');
  heading.className = 'viewport-warning-heading';
  heading.textContent = 'Desktop Display Required';
  card.appendChild(heading);

  const msg = document.createElement('p');
  msg.className = 'viewport-warning-message';
  msg.textContent =
    'Sentinel is designed for desktop use. Please use a screen width of 1280px or greater for the best experience.';
  card.appendChild(msg);

  const hint = document.createElement('p');
  hint.className = 'viewport-warning-hint';
  hint.textContent = `Current width: ${window.innerWidth}px`;
  card.appendChild(hint);

  const btn = document.createElement('button');
  btn.className = 'viewport-warning-btn';
  btn.textContent = 'Continue anyway';
  btn.setAttribute('aria-label', 'Dismiss viewport warning and continue');
  btn.addEventListener('click', () => {
    dismiss();
  });
  card.appendChild(btn);

  overlay.appendChild(card);
  return overlay;
}

/** Show the overlay if viewport is too small and not dismissed. */
function showOverlay(): void {
  if (overlayEl) return;
  overlayEl = createOverlay();
  document.body.appendChild(overlayEl);
}

/** Remove the overlay and persist dismissal. */
function dismiss(): void {
  localStorage.setItem(DISMISS_KEY, '1');
  if (overlayEl) {
    overlayEl.style.opacity = '0';
    setTimeout(() => {
      overlayEl?.remove();
      overlayEl = null;
    }, 200);
  }
}

/** Remove overlay without persisting (e.g. when viewport grows). */
function hideOverlay(): void {
  if (overlayEl) {
    overlayEl.style.opacity = '0';
    setTimeout(() => {
      overlayEl?.remove();
      overlayEl = null;
    }, 200);
  }
}

/** Evaluate viewport width and show/hide overlay accordingly. */
function evaluate(): void {
  if (isDismissed()) return;

  if (window.innerWidth < MIN_WIDTH) {
    showOverlay();
    // Update the hint text with current width
    const hint = overlayEl?.querySelector('.viewport-warning-hint');
    if (hint) {
      hint.textContent = `Current width: ${window.innerWidth}px`;
    }
  } else {
    hideOverlay();
  }
}

/**
 * Initialize viewport detection.
 * Checks on boot and on window resize (debounced).
 */
export function initViewportDetection(): void {
  evaluate();

  let resizeTimer: ReturnType<typeof setTimeout> | null = null;
  window.addEventListener('resize', () => {
    if (resizeTimer) clearTimeout(resizeTimer);
    resizeTimer = setTimeout(evaluate, 250);
  });
}
